import { useCallback, useEffect, useId, useMemo, useRef, useState, type JSX, type ReactNode } from "react";
import {
  EMPTY_FILTER,
  filterDefinitionSchema,
  filterJsonSchema,
  isEmptyFilter,
  type FilterDefinition,
  type LeadSortField,
  type SmartListDetail,
  type SmartListRevision,
  type SmartListSort,
} from "@gioia365/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useBlocker, useNavigate, useParams, useRouteContext, useSearch } from "@tanstack/react-router";
import { ApiRequestError } from "../api/http";
import { teamQuery, useTab } from "../api/queries";
import { callBlockOf, OtherSessionDialog, savedText, startErrorText, useDialerStart, type StartMode } from "../dialer/dialer-start";
import { useOptionalDialerSession } from "../dialer/dialer-session";
import { sourceText } from "../dialer/dialer-texts";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { PageHeader } from "../design/components/PageHeader";
import { Reason } from "../design/components/Reason";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatCount, formatNumericDateTime } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { ColumnsDrawer } from "../leads/columns/ColumnsDrawer";
import { useTableLayout } from "../leads/columns/layout-queries";
import { FilterBar } from "../leads/filters/FilterBar";
import { leadCatalogQuery } from "../leads/filters/filter-queries";
import { parseFilterParam } from "../leads/filters/filter-url";
import { validateLeadListSearch } from "../leads/LeadListPage";
import { LOADING_ROWS, SEARCH_MAX_LENGTH, useColumnArrange, useLeadPages, useResultSearch } from "../leads/lead-list-hooks";
import { leadListQuery, useLeadView } from "../leads/lead-queries";
import { leadName } from "../leads/lead-display";
import { LeadTable } from "../leads/LeadTable";
import "../leads/lead-list.css";
import { smartListScopeOf, useAccess } from "../session/access";
import { tabKey } from "../session/tab-context";
import { HistoryDialog } from "./HistoryDialog";
import { NewSmartListDialog } from "./NewSmartListDialog";
import { personStatusWord, ShareDialog } from "./ShareDialog";
import { DeleteDialog, LeaveListDialog, MoreMenu, RenameDialog, SaveConfirmDialog, useChangeFields, type MoreAction } from "./SmartListDialogs";
import {
  forgetSmartList,
  pickSmartList,
  recordSmartListOpening,
  rememberedSmartList,
  savedListSessionQuery,
  savedSessionMarksQuery,
  smartListKeys,
  smartListLeadsQuery,
  smartListQuery,
  smartListsQuery,
  smartListTableScope,
  useArchiveSmartList,
  useSaveSmartList,
} from "./smart-list-queries";
import { describeChanges, draftChanges, namesText, sortText, visibilityText, type ListDraft } from "./smart-list-text";
import "./smart-lists.css";

// Le pagine delle Smart List (piano M3 Task 8 e 9). `/smart-list` è la voce della rail: la guardia della rotta apre
// l'ultima lista aperta nella scheda, altrimenti la prima del menu; questa pagina resta soltanto senza liste, e spiega
// come nasce una lista, oppure quando il menu non si legge.
//
// `/smart-list/$smartListId` è la pagina di una lista (Task 9, mock-up `smart-list.html` e `initSmartList`). La bozza
// (filtri e ordine di chiamata) parte dalla lista salvata e vive nell'indirizzo (`?filter=&sort=&direction=`, sempre
// tutti e tre quando c'è; il filtro come oggetto JSON scritto una volta sola, decisione P25): una ricarica, un link o
// «Indietro» la ritrovano. «Modifiche non salvate» quando la bozza è
// diversa dalla lista salvata (`draftChanges`, con le regole dello storico del server). I risultati sono sempre
// `GET /api/leads` con `who = all` (decisione P5): la definizione nel perimetro di chi guarda, lo stesso numero del menu;
// la pagina non offre «I miei / Tutti». La ricerca nei risultati vive nell'indirizzo (`q`) ma non entra nella lista.
// «Salva» (con «Cosa cambia per chi la usa» per una lista condivisa o pubblica), «Salva come nuova», «Annulla modifiche»,
// «Condividi», «Gestisci lista» (Rinomina, Storico della lista, Elimina la lista) e «Inizia a chiamare». Uscire con una bozza chiede
// prima «Non hai salvato le modifiche» (guardia del router e `beforeunload`).
// «Inizia a chiamare» (piano M5, Task 6; mock-up `initSmartList`) avvia il Power Dialer del guscio (`DialerSessionProvider`)
// sulla lista salvata e apre la scheda del primo Lead; sotto, da chi si parte. È spento, con il perché, nella «Guarda come…»
// e nel contesto di piattaforma (le parole di «Chiama», ruling Q27: lì il Power Dialer non si chiede mai), senza «Chiamare e
// usare la postazione», con modifiche non salvate e con la lista vuota. Con una sessione interrotta su questa lista (`GET
// /api/dialer-sessions/saved`) diventa «Riprendi a chiamare», con accanto «Ricomincia da capo» (ruling Q6) e la riga di quando
// e da chi si riparte; i Lead già fatti in quella sessione portano il loro segno nella tabella. La telefonia spenta si scopre
// provando: il rifiuto del server (`503`, ruling Q10: nulla è riservato) si dice sotto il pulsante. Con una sessione viva su
// un'altra sorgente il server risponde `409 dialer_session_active` e la finestra chiede se interromperla per chiamare da qui.
// Con una sessione viva su questa stessa lista non si avvia niente (ruling Q47): «Sessione in corso su …» con «Torna alla
// scheda», come la barra, che riapre il suo Lead; interromperla e ripartire la ricomincerebbe da capo (ruling Q6).
// Le colonne sono la vista della lista (`smart_list`), sul dispositivo finché non si salvano: non toccano la lista.
// Aperta la lista, la pagina lo dice al server (`recordSmartListOpening`): le «novità» della lista nel menu tornano a zero
// per chi la apre per sé, mai nella Vista Commerciale (riscontro del product owner del 3 ottobre 2026).

const LIST_ROUTE = "/autenticato/guscio/azienda/smart-list/$smartListId";

/** I parametri dell'indirizzo di una lista: quelli dell'elenco Lead senza «Di chi», che una lista non ha (decisione P5). */
export type SmartListSearch = {
  q?: string;
  sort?: LeadSortField;
  direction?: "asc" | "desc";
  /**
   * La bozza dei filtri, come oggetto; anche «nessun filtro», quando la bozza li ha tolti tutti. Il testo JSON dei link
   * di prima si legge ancora (decisione P25).
   */
  filter?: FilterDefinition;
};

/** La bozza dei filtri dell'indirizzo; una definizione vuota scritta apposta resta (tutti i filtri tolti), una sbagliata no. */
function draftFilterParam(raw: unknown): FilterDefinition | undefined {
  if (raw === undefined) return undefined;
  const definition = parseFilterParam(raw);
  if (!isEmptyFilter(definition)) return definition;
  const parsed = typeof raw === "string" ? filterJsonSchema.safeParse(raw) : filterDefinitionSchema.safeParse(raw);
  return parsed.success && isEmptyFilter(parsed.data) ? EMPTY_FILTER : undefined;
}

export function validateSmartListSearch(search: Record<string, unknown>): SmartListSearch {
  const parsed = validateLeadListSearch(search);
  const result: SmartListSearch = {};
  if (parsed.q !== undefined) result.q = parsed.q;
  if (parsed.sort !== undefined) result.sort = parsed.sort;
  if (parsed.direction !== undefined) result.direction = parsed.direction;
  const filter = draftFilterParam(search.filter);
  if (filter !== undefined) result.filter = filter;
  return result;
}

/** La bozza dell'indirizzo, oppure la lista salvata per la parte che l'indirizzo non ha. */
function draftOf(search: SmartListSearch, list: Pick<SmartListDetail, "definition" | "sort">): ListDraft {
  return {
    // Di nuovo dalla forma: un valore scartato da `validateSmartListSearch` resta quello grezzo dell'indirizzo.
    definition: draftFilterParam(search.filter) ?? list.definition,
    sort: search.sort ? { field: search.sort, direction: search.direction ?? "asc" } : list.sort,
  };
}

/** L'indirizzo con una bozza: filtri e ordine di chiamata, sempre insieme, così una lista ricaricata non la cambia. */
function withDraft(previous: SmartListSearch, draft: ListDraft): SmartListSearch {
  return { ...previous, filter: draft.definition, sort: draft.sort.field, direction: draft.sort.direction };
}

/** L'indirizzo senza bozza: la lista salvata. La ricerca nei risultati resta. */
function withoutDraft(previous: SmartListSearch): SmartListSearch {
  return { ...previous, filter: undefined, sort: undefined, direction: undefined };
}

/** `/smart-list` senza liste da aprire: come nasce una lista, con «Vai all'elenco Lead»; oppure l'attesa o l'errore. */
export function SmartListsStartPage(): JSX.Element {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const tabStorage = useRouteContext({ from: "/autenticato/guscio/azienda/smart-list", select: (context) => context.tabStorage });
  const canView = smartListScopeOf(access, "view") !== "none";
  const menu = useQuery({ ...smartListsQuery(tab), enabled: canView });
  // Le liste arrivate dopo la guardia (il menu che non rispondeva, poi «Riprova»): si apre quella giusta.
  const target = menu.data ? pickSmartList(menu.data.lists, rememberedSmartList(tabStorage)) : null;
  if (target) return <Navigate to="/smart-list/$smartListId" params={{ smartListId: target.id }} replace />;

  let content: ReactNode;
  if (!canView) content = <Reason tone="warning">{t("errors.permission_denied")}</Reason>;
  else if (menu.isError) {
    content = (
      <div className="g-smart-list__problem">
        <Reason tone="warning" role="alert">{messageForError(t, menu.error)}</Reason>
        <Button onClick={() => void menu.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (!menu.data) content = <p>{t("common.loading")}</p>;
  else {
    content = (
      <div className="g-smart-list__empty">
        <p>{t("smartLists.noLists")}</p>
        <Link to="/lead" className="g-button" data-variant="primary">
          {t("smartLists.goToLeads")}
        </Link>
      </div>
    );
  }

  return (
    <div className="g-smart-list">
      <PageHeader eyebrow={access.context.organization.name} title={t("shell.nav.smartLists")} />
      {content}
    </div>
  );
}

/** La pagina di una lista: la lista, oppure l'attesa, «Smart List non trovata» o l'errore con «Riprova». */
export function SmartListPage(): JSX.Element {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const { smartListId } = useParams({ from: LIST_ROUTE });
  const detail = useQuery(smartListQuery(tab, smartListId));
  // Una lista eliminata, o che chi guarda non vede più, non torna riprovando.
  const gone = detail.isError && !detail.data && detail.error instanceof ApiRequestError && detail.error.code === "smart_list_not_found";
  const menu = useQuery({ ...smartListsQuery(tab), enabled: gone });
  const refetch = detail.refetch;
  const reload = useCallback(() => refetch(), [refetch]);

  if (detail.data) return <SmartListView key={smartListId} list={detail.data} onReload={reload} />;

  if (gone) {
    const other = menu.data ? pickSmartList(menu.data.lists.filter((list) => list.id !== smartListId), null) : null;
    return (
      <div className="g-smart-list">
        <PageHeader eyebrow={t("shell.nav.smartLists")} title={t("smartLists.page.notFoundTitle")} />
        <div className="g-smart-list__problem">
          <p>{t("smartLists.page.notFoundText")}</p>
          {other ? (
            <Link to="/smart-list/$smartListId" params={{ smartListId: other.id }} className="g-button" data-variant="primary">
              {t("smartLists.page.openList", { name: other.name })}
            </Link>
          ) : menu.data || menu.isError ? (
            <Link to="/lead" className="g-button" data-variant="primary">
              {t("smartLists.goToLeads")}
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="g-smart-list">
      <PageHeader eyebrow={access.context.organization.name} title={t("shell.nav.smartLists")} />
      {detail.isError ? (
        <div className="g-smart-list__problem">
          <Reason tone="warning" role="alert">{messageForError(t, detail.error)}</Reason>
          <Button onClick={() => void detail.refetch()}>{t("common.retry")}</Button>
        </div>
      ) : (
        <p>{t("common.loading")}</p>
      )}
    </div>
  );
}

type OpenDialog = "share" | "rename" | "history" | "delete" | "saveAs" | "columns";

function SmartListView({ list, onReload }: { list: SmartListDetail; onReload: () => Promise<unknown> }): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const access = useAccess();
  const view = useLeadView();
  const toast = useToast();
  const search = useSearch({ from: LIST_ROUTE });
  const navigate = useNavigate({ from: "/smart-list/$smartListId" });
  const baseId = useId();
  const searchId = `${baseId}-search`;
  const startReasonId = `${baseId}-start`;
  const readOnlyId = `${baseId}-read-only`;
  const searchRef = useRef<HTMLInputElement>(null);
  const organization = access.context.organization.name;
  const timeZone = access.context.settings.timeZone;
  const me = tab.kind === "membership" ? tab.membershipId : null;
  const { canSave, canCreate } = list.permissions;

  // --- l'apertura: una volta per lista, con la scheda e la cache di adesso (la vista nuova rifà la pagina) ---
  const queryClient = useQueryClient();
  const openedWith = useRef({ queryClient, tab });
  useEffect(() => {
    openedWith.current = { queryClient, tab };
  });
  useEffect(() => {
    const { queryClient: client, tab: current } = openedWith.current;
    void recordSmartListOpening(client, current, list.id);
  }, [list.id]);

  // --- la lista salvata e la bozza ---
  const saved = useMemo<ListDraft>(() => ({ definition: list.definition, sort: list.sort }), [list.definition, list.sort]);
  const draft = useMemo(() => draftOf(search, list), [search, list]);
  const changes = useMemo(() => draftChanges(saved, draft), [saved, draft]);
  const dirty = changes.length > 0;

  // --- le colonne: la vista della lista (D5), che non tocca la lista ---
  const catalog = useQuery(leadCatalogQuery(tab));
  const scope = useMemo(() => smartListTableScope(list.id), [list.id]);
  const layout = useTableLayout({ tab, scope, t, catalog });
  const { tableColumns, widths, fields, include, moveColumn, resizeColumn, hideColumn } = useColumnArrange(layout);

  // --- i Lead: la bozza con la ricerca nei risultati, e la bozza da sola (il numero della lista) ---
  const q = search.q?.trim() ?? "";
  const base = useMemo(() => smartListLeadsQuery({ definition: draft.definition, sort: draft.sort, fields, include, q }), [draft, fields, include, q]);
  // Una colonna in più o in meno rilegge le righe, ma la tabella non torna in cima.
  const scrollKey = JSON.stringify({ ...base, fields: undefined, include: undefined });
  const { first, total, rowAt, handleRange, failedPages } = useLeadPages({ tab, base, view });
  // Senza ricerca è la stessa lettura della prima pagina (la stessa chiave): nessuna richiesta in più.
  const whole = useQuery({
    ...leadListQuery(tab, { ...smartListLeadsQuery({ definition: draft.definition, sort: draft.sort, fields, include, q: "" }), offset: 0 }, view),
    placeholderData: keepPreviousData,
  });
  const listTotal = whole.data?.total;

  // --- la ricerca nei risultati: l'indirizzo si aggiorna 200 ms dopo l'ultimo tasto; non entra nella lista ---
  const writeQ = useCallback((next: string | undefined) => void navigate({ search: (previous) => ({ ...previous, q: next }), replace: true }), [navigate]);
  const { text, setText } = useResultSearch(search.q, writeQ);

  function clearSearch() {
    setText("");
    void navigate({ search: (previous) => ({ ...previous, q: undefined }), replace: true });
    searchRef.current?.focus();
  }

  // --- scrivere e togliere la bozza: ogni cambio riscrive l'indirizzo, senza una voce in più nella cronologia ---
  const listRef = useRef(list);
  useEffect(() => {
    listRef.current = list;
  });
  const writeDraft = useCallback(
    (change: Partial<ListDraft>) => {
      void navigate({
        search: (previous) => {
          const current = draftOf(previous, listRef.current);
          return withDraft(previous, { definition: change.definition ?? current.definition, sort: change.sort ?? current.sort });
        },
        replace: true,
      });
    },
    [navigate],
  );
  // Togliere la bozza apposta («Annulla modifiche», dopo «Salva») non chiede di salvarla.
  const clearDraft = useCallback(() => {
    void navigate({ search: withoutDraft, replace: true, ignoreBlocker: true });
  }, [navigate]);

  const changeFilter = useCallback((next: FilterDefinition) => writeDraft({ definition: next }), [writeDraft]);
  const resetFilters = useCallback(() => writeDraft({ definition: EMPTY_FILTER }), [writeDraft]);
  function changeSort(field: LeadSortField, chosen?: SmartListSort["direction"]) {
    const direction = chosen ?? (draft.sort.field === field && draft.sort.direction === "asc" ? "desc" : "asc");
    writeDraft({ sort: { field, direction } });
  }

  // --- uscire con modifiche non salvate (mock-up `leaveGuard`): un'altra pagina, un'altra lista, un'altra azienda, o la
  // stessa lista senza la bozza (il suo nome nel menu). Cambiare la bozza o cercare nei risultati non è un'uscita. ---
  const dirtyNow = useRef(dirty);
  // La bozza di adesso, per il salvataggio che finisce dopo: una modifica fatta mentre aspettava il server resta.
  const draftNow = useRef(draft);
  useEffect(() => {
    dirtyNow.current = dirty;
    draftNow.current = draft;
  });
  const shouldBlockFn = useCallback(({ current, next }: { current: { pathname: string }; next: { pathname: string; search: unknown } }) => {
    if (!dirtyNow.current) return false;
    if (current.pathname !== next.pathname) return true;
    const search = (typeof next.search === "object" && next.search !== null ? next.search : {}) as SmartListSearch;
    return search.filter === undefined && search.sort === undefined;
  }, []);
  const enableBeforeUnload = useCallback(() => dirtyNow.current, []);
  const blocker = useBlocker({ shouldBlockFn, enableBeforeUnload, withResolver: true });

  // --- salvare ---
  const save = useSaveSmartList(tab, list.id);
  const [confirming, setConfirming] = useState<{ leaving: boolean } | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);

  async function doSave(leaving: boolean) {
    if (save.isPending) return;
    setSaveError(null);
    const sent = draft;
    try {
      const written = await save.mutateAsync({ expectedRevision: list.revision, definition: sent.definition, sort: sent.sort });
      setConfirming(null);
      setConflict(false);
      toast.show(t("smartLists.page.savedVersion", { revision: written.revision }), "success");
      if (leaving) {
        if (blocker.status === "blocked") blocker.proceed();
        return;
      }
      // L'indirizzo non riporta più una bozza: è la lista salvata. Se intanto la bozza è cambiata, quella nuova resta da salvare
      // (rilievo di Codex sulla #62, turno di notte del 10 ottobre 2026).
      if (draftChanges(sent, draftNow.current).length === 0) clearDraft();
    } catch (error) {
      setConfirming(null);
      if (leaving && blocker.status === "blocked") blocker.reset();
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        // Un collega ha salvato prima: la lista si rilegge, la bozza resta nell'indirizzo.
        setConflict(true);
        void onReload();
        return;
      }
      setSaveError(error);
    }
  }

  /** «Salva»: una lista che usano anche altri passa prima da «Cosa cambia per chi la usa»; una privata si salva subito. */
  function startSave(leaving = false) {
    if (!dirty || !canSave) {
      if (leaving && blocker.status === "blocked") blocker.reset();
      return;
    }
    if (list.visibility !== "private") setConfirming({ leaving });
    else void doSave(leaving);
  }

  function discard() {
    setConflict(false);
    setSaveError(null);
    clearDraft();
  }

  // --- «Gestisci lista», lo storico, l'eliminazione ---
  const archive = useArchiveSmartList(tab, list.id);
  async function deleteList() {
    if (archive.isPending) return;
    try {
      await archive.mutateAsync();
    } catch {
      // L'errore resta nella finestra (`archive.error`).
      return;
    }
    toast.show(t("smartLists.page.deleted", { name: list.name }), "success");
    // La lista non c'è più: la bozza non ha dove restare. Prima si esce (la voce «Smart List» apre la prima lista
    // rimasta), poi la lista esce dalla cache: mentre la pagina è ancora qui non si rilegge, e non dice «non trovata».
    await navigate({ to: "/smart-list", ignoreBlocker: true });
    forgetSmartList(queryClient, tab, list.id);
  }

  function restore(revision: SmartListRevision) {
    setDialog(null);
    const next: ListDraft = { definition: revision.definition, sort: revision.sort };
    if (draftChanges(saved, next).length === 0) {
      clearDraft();
      toast.show(t("smartLists.page.restoredSame", { revision: revision.revision }));
      return;
    }
    void navigate({ search: (previous) => withDraft(previous, next), replace: true });
    toast.show(canSave ? t("smartLists.page.restored", { revision: revision.revision }) : t("smartLists.page.restoredReadOnly", { revision: revision.revision }));
  }

  function choose(action: MoreAction) {
    setDialog(action);
  }

  // --- «Inizia a chiamare», «Riprendi a chiamare», «Ricomincia da capo» (piano M5, Task 6; `dialer-start.tsx`) ---
  const shellDialer = useOptionalDialerSession();
  const callBlock = callBlockOf(t, access, tab);
  // Chi chiama da questa scheda: soltanto allora si chiede la sessione salvata (mai nella vista né dalla piattaforma).
  const caller = callBlock === null && shellDialer !== null ? tab : null;
  const savedQuery = useQuery(savedListSessionQuery(caller, list.id));
  const callerKey = caller ? tabKey(caller) : null;
  const { liveHere, starting, startError, startCalling, other } = useDialerStart({
    caller,
    source: { type: "smart_list", smartListId: list.id },
    // La sessione viva del guscio finisce mentre la lista è aperta (interrotta dalla barra, finita, interrotta dal giro del
    // server): quella salvata di questa lista può essere lei, e si rilegge. Riaperta, la pagina la rilegge comunque.
    onLiveEnded: () => {
      if (callerKey !== null) void queryClient.invalidateQueries({ queryKey: smartListKeys.savedSession(callerKey, list.id) });
    },
    // La sessione salvata può essere cambiata intanto (ripresa o ricominciata da un'altra scheda): si rilegge.
    onRefused: () => void savedQuery.refetch(),
  });
  // Con modifiche non salvate la sessione salvata non si offre (mock-up): prima si salva, o si annulla.
  const savedSession = caller !== null && !dirty ? (savedQuery.data ?? null) : null;
  const marksQuery = useQuery(savedSessionMarksQuery(savedSession ? caller : null, list.id, savedSession));
  const marks = useMemo(() => {
    if (!savedSession || !marksQuery.data) return undefined;
    const called = t("smartLists.page.workedCalled");
    const skipped = t("smartLists.page.workedSkipped");
    return Object.fromEntries(Object.entries(marksQuery.data).map(([leadId, mark]) => [leadId, mark === "called" ? called : skipped]));
  }, [savedSession, marksQuery.data, t]);

  // --- le parole ---
  const changeFields = useChangeFields({ tab, catalog: catalog.data, columns: layout.catalog, conditions: [...list.definition.conditions, ...draft.definition.conditions] });
  const ownerTeam = useQuery({ ...teamQuery(tab), enabled: !list.owner.active });
  const ownerMember = ownerTeam.data?.find((row) => row.kind === "membership" && row.membershipId === list.owner.membershipId);
  let ownerText = t("smartLists.page.owner", { name: list.owner.displayName });
  if (!list.owner.active && (ownerTeam.data || ownerTeam.isError)) {
    const status = personStatusWord(t, ownerMember?.kind === "membership" ? ownerMember : undefined);
    ownerText = t("smartLists.page.ownerWithStatus", { name: list.owner.displayName, status });
  }
  const countText = (value: number) => (value === 1 ? t("smartLists.countOne") : t("smartLists.countMany", { count: formatCount(locale, value) }));
  const count = listTotal === undefined ? t("common.loading") : countText(listTotal);
  const callOrder = sortText(t, layout.catalog, draft.sort);

  // «Inizia a chiamare»: il primo motivo che vale, poi da chi si parte (o, con la sessione salvata, da chi si riprende).
  // Con la sessione viva su questa lista, la riga della barra.
  let startBlock = liveHere ? t("dialer.restored", { source: sourceText(t, liveHere) }) : callBlock;
  if (startBlock === null && dirty) startBlock = t("smartLists.page.startDirty");
  if (startBlock === null && listTotal === 0) startBlock = t("smartLists.page.startEmpty");
  const firstRow = listTotal !== undefined && listTotal > 0 && !whole.isPlaceholderData ? whole.data?.rows[0] : undefined;
  // Finché non si sa se c'è una sessione salvata, niente parte: «Inizia a chiamare» la ricomincerebbe da capo (ruling Q6).
  const savedKnown = caller === null || dirty || savedQuery.isSuccess;
  let startLine: string | null = startBlock;
  if (startLine === null && savedKnown) {
    if (savedSession) startLine = savedText(t, locale, timeZone, savedSession, t("smartLists.page.startEmpty"));
    else if (firstRow) startLine = t("smartLists.page.startFrom", { lead: leadName(firstRow) ?? "—", count: formatCount(locale, listTotal ?? 0) });
  }
  const startMode: StartMode = savedSession ? "resume" : "start";
  const startOff = startBlock !== null || !savedKnown || firstRow === undefined || (savedSession !== null && savedSession.next === null) || starting !== null;
  const restartOff = startBlock !== null || listTotal === undefined || starting !== null;

  // «Cosa cambia per chi la usa»: chi la usa e i Lead di prima e di dopo, per chi guarda.
  const savedCount = useQuery({
    ...leadListQuery(tab, { ...smartListLeadsQuery({ definition: saved.definition, sort: saved.sort, fields, include, q: "" }), offset: 0 }, view),
    enabled: confirming !== null,
  });
  function usersText(): string {
    if (list.visibility === "public_team") return t("smartLists.page.confirmUsersTeam", { organization });
    const names = [list.owner, ...list.recipients].filter((person) => person.membershipId !== me).map((person) => person.displayName);
    return names.length > 0 ? namesText(locale, names) : t("smartLists.page.confirmUsersShared");
  }

  let content: ReactNode;
  const data = first.data;
  if (first.isError && !data) {
    // Una bozza che il server rifiuta (un campo tolto nel frattempo) non passa riprovando: si torna alla lista salvata.
    const filterRefused = first.error instanceof ApiRequestError && first.error.code === "filter_invalid" && dirty;
    content = (
      <div className="g-lead-list__problem">
        <Reason tone="warning" role="alert">{messageForError(t, first.error)}</Reason>
        {filterRefused ? <Button onClick={discard}>{t("smartLists.page.discard")}</Button> : <Button onClick={() => void first.refetch()}>{t("common.retry")}</Button>}
      </div>
    );
  } else if (data && total === 0 && !first.isPlaceholderData) {
    content = q ? (
      <div className="g-lead-list__empty">
        <p className="g-lead-list__empty-title">{t("smartLists.page.noSearchResults")}</p>
        <Button onClick={clearSearch}>{t("smartLists.page.clearSearch")}</Button>
      </div>
    ) : (
      <div className="g-lead-list__empty">
        <p className="g-lead-list__empty-title">{t("smartLists.page.empty")}</p>
        <p>{t("smartLists.page.emptyHint")}</p>
      </div>
    );
  } else {
    content = (
      <>
        {failedPages.length > 0 ? (
          <div className="g-lead-list__problem">
            <Reason tone="warning" role="alert">{t("leadList.pagesFailed")}</Reason>
            <Button onClick={() => failedPages.forEach((result) => void result.refetch())}>{t("common.retry")}</Button>
          </div>
        ) : null}
        <LeadTable
          caption={t("smartLists.page.caption", { sort: callOrder })}
          total={data ? total : LOADING_ROWS}
          totalKnown={data !== undefined}
          rowAt={rowAt}
          sort={draft.sort}
          onSort={changeSort}
          onRangeChange={handleRange}
          resetKey={scrollKey}
          busy={first.isPlaceholderData || first.isPending}
          timeZone={timeZone}
          columns={tableColumns}
          widths={widths}
          onMove={moveColumn}
          onResize={resizeColumn}
          onHide={hideColumn}
          // Durante la ricerca il primo trovato non è il primo da chiamare: niente numero d'ordine (decisione P30).
          position={q === ""}
          marks={marks}
        />
      </>
    );
  }

  const leaving = blocker.status === "blocked";

  return (
    <div className="g-lead-list g-smart-page">
      <header className="g-smart-page__head">
        <div className="g-smart-page__titles">
          <p className="g-page-head__eyebrow">{t("shell.nav.smartLists")}</p>
          <div className="g-smart-page__title-row">
            <h1 className="g-page-head__title">{list.name}</h1>
            <span className="g-smart-page__count" role="status">
              {count}
            </span>
          </div>
          <p className="g-smart-page__meta">
            <span>
              <Icon name="filter" />
              {t("smartLists.page.dynamic")}
            </span>
            <span>{visibilityText(t, list.visibility)}</span>
            <span>{ownerText}</span>
            <span>{t("smartLists.page.savedAtBy", { when: formatNumericDateTime(timeZone, list.savedAt), who: list.savedBy.displayName })}</span>
            <span className="g-smart-page__state" data-state={dirty ? "dirty" : "saved"} role="status">
              <Icon name={dirty ? "info" : "check"} />
              {dirty ? t("smartLists.page.dirty") : t("smartLists.page.saved")}
            </span>
          </p>
        </div>
        <div className="g-smart-page__actions">
          <Button aria-haspopup="dialog" onClick={() => setDialog("share")}>
            <Icon name="user" />
            {t("smartLists.page.share")}
          </Button>
          <MoreMenu list={list} onChoose={choose} />
          {dirty ? <Button onClick={discard}>{t("smartLists.page.discard")}</Button> : null}
          <Button
            disabled={!canSave || !dirty || save.isPending}
            aria-describedby={canSave ? undefined : readOnlyId}
            aria-busy={save.isPending ? "true" : undefined}
            onClick={() => startSave()}
          >
            {t("smartLists.page.save")}
          </Button>
          {canCreate ? (
            <Button aria-haspopup="dialog" onClick={() => setDialog("saveAs")}>
              {t("smartLists.page.saveAs")}
            </Button>
          ) : null}
          {liveHere?.current ? (
            <Button
              variant="primary"
              aria-describedby={startReasonId}
              onClick={() => {
                if (liveHere.current) void navigate({ to: "/lead/$leadId", params: { leadId: liveHere.current.id } });
              }}
            >
              {t("dialer.backToLead")}
            </Button>
          ) : null}
          {liveHere === null && savedSession ? (
            <Button
              disabled={restartOff}
              aria-describedby={startLine === null ? undefined : startReasonId}
              aria-busy={starting === "restart" ? "true" : undefined}
              onClick={() => void startCalling("restart")}
            >
              {t("smartLists.page.restart")}
            </Button>
          ) : null}
          {liveHere === null ? (
            <Button
              variant="primary"
              disabled={startOff}
              aria-describedby={startLine === null ? undefined : startReasonId}
              aria-busy={starting === startMode ? "true" : undefined}
              onClick={() => void startCalling(startMode)}
            >
              <Icon name="phone" />
              {savedSession ? t("smartLists.page.resume") : t("smartLists.page.start")}
            </Button>
          ) : null}
          <div className="g-smart-page__reasons">
            {startLine !== null ? (
              <Reason id={startReasonId} tone={dirty && callBlock === null && liveHere === null ? "warning" : "info"}>
                {startLine}
              </Reason>
            ) : null}
            {caller !== null && !dirty && liveHere === null && savedQuery.isError ? (
              <div className="g-smart-page__problem">
                <Reason tone="warning" role="alert">
                  {messageForError(t, savedQuery.error)}
                </Reason>
                <Button onClick={() => void savedQuery.refetch()}>{t("common.retry")}</Button>
              </div>
            ) : null}
            {startError ? (
              <Reason tone="warning" role="alert">
                {startErrorText(t, startError)}
              </Reason>
            ) : null}
            {canSave ? null : <Reason id={readOnlyId}>{t("smartLists.page.readOnly", { owner: list.owner.displayName })}</Reason>}
          </div>
        </div>
      </header>

      {conflict ? (
        <Reason tone="warning" role="alert">
          {t("smartLists.page.conflict")}
        </Reason>
      ) : null}
      {saveError ? (
        <Reason tone="warning" role="alert">
          {messageForError(t, saveError)}
        </Reason>
      ) : null}

      <div className="g-lead-list__toolbar">
        <div className="g-smart-search">
          <TextField
            ref={searchRef}
            id={searchId}
            label={t("smartLists.page.search")}
            hideLabel
            icon="search"
            type="search"
            value={text}
            maxLength={SEARCH_MAX_LENGTH}
            autoComplete="off"
            placeholder={t("smartLists.page.search")}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        <Button aria-haspopup="dialog" onClick={() => setDialog("columns")}>
          <Icon name="columns" />
          {t("tableView.button")}
        </Button>
      </div>
      {list.staleConditions > 0 ? (
        <Reason tone="warning">
          {list.staleConditions === 1 ? t("smartLists.page.staleOne") : t("smartLists.page.staleMany", { count: list.staleConditions })}
        </Reason>
      ) : null}
      <FilterBar
        definition={draft.definition}
        onChange={changeFilter}
        onReset={resetFilters}
        catalog={catalog.data}
        tab={tab}
        resultText={count}
        narrowed={q !== ""}
      />
      {q && data && listTotal !== undefined ? (
        <p className="g-smart-page__note">{t("smartLists.page.searchNote", { shown: formatCount(locale, total), total: formatCount(locale, listTotal) })}</p>
      ) : null}
      {listTotal !== undefined && listTotal > 0 ? (
        <p className="g-smart-page__queue">
          <Icon name="phone" />
          <span>{t("smartLists.page.queue", { sort: callOrder })}</span>
        </p>
      ) : null}
      {content}

      {confirming !== null ? (
        <SaveConfirmDialog
          people={usersText()}
          lines={describeChanges(t, changes, changeFields)}
          before={savedCount.data && !savedCount.isPlaceholderData ? formatCount(locale, savedCount.data.total) : null}
          // Mentre arriva il numero della bozza resta in vista quello di prima: non si scrive.
          after={listTotal === undefined || whole.isPlaceholderData ? null : formatCount(locale, listTotal)}
          saving={save.isPending}
          onCancel={() => {
            const wasLeaving = confirming.leaving;
            setConfirming(null);
            if (wasLeaving && blocker.status === "blocked") blocker.reset();
          }}
          onSave={() => void doSave(confirming.leaving)}
        />
      ) : null}
      {leaving && confirming === null && !save.isPending ? (
        <LeaveListDialog
          canSave={canSave}
          canCreate={canCreate}
          onStay={() => {
            if (blocker.status === "blocked") blocker.reset();
          }}
          onLeave={() => {
            if (blocker.status === "blocked") blocker.proceed();
          }}
          onSaveAs={() => {
            if (blocker.status === "blocked") blocker.reset();
            setDialog("saveAs");
          }}
          onSaveAndLeave={() => startSave(true)}
        />
      ) : null}
      {other ? <OtherSessionDialog other={other} id={baseId} /> : null}
      {dialog === "saveAs" ? (
        <NewSmartListDialog
          tab={tab}
          definition={draft.definition}
          sort={draft.sort}
          initialName={t("smartLists.page.copyName", { name: list.name })}
          variant="saveAs"
          // «La nuova è tua»: soltanto il Super admin di Gioia365, senza un'appartenenza, la fa per un Commerciale.
          forOthers={me === null && smartListScopeOf(access, "update") === "all"}
          mineActive={false}
          onCreated={(smartListId) => {
            setDialog(null);
            // La bozza ora è la lista nuova: si apre senza chiedere di salvarla.
            void navigate({ to: "/smart-list/$smartListId", params: { smartListId }, ignoreBlocker: true });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "share" ? (
        <ShareDialog
          tab={tab}
          list={list}
          organization={organization}
          canChangeOwner={smartListScopeOf(access, "update") === "all"}
          fields={changeFields}
          onReload={onReload}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "rename" ? <RenameDialog tab={tab} list={list} me={me} onReload={onReload} onClose={() => setDialog(null)} /> : null}
      {dialog === "history" ? (
        <HistoryDialog
          tab={tab}
          list={list}
          catalog={catalog.data}
          columns={layout.catalog}
          timeZone={timeZone}
          onRestore={restore}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "delete" ? (
        <DeleteDialog
          name={list.name}
          deleting={archive.isPending}
          error={archive.error}
          onCancel={() => {
            archive.reset();
            setDialog(null);
          }}
          onConfirm={() => void deleteList()}
        />
      ) : null}
      {dialog === "columns" ? (
        <ColumnsDrawer
          tab={tab}
          scope={scope}
          organization={organization}
          context={t("tableView.contextList", { name: list.name })}
          layout={layout}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
